/**
 * Owner Console pure logic (Macro #29 Stage 3 — Phase D).
 *
 * Framework-free so the FROZEN owner laws are exhaustively unit-testable:
 *   * Confirmation law: <=1000 slots → normal double-confirm; >1000 slots →
 *     strong warning + manual re-entry; GRID ZERO → maximum confirmation
 *     regardless of quantity.
 *   * Owner actions are FAIL CLOSED unless the backend advertises the capability.
 *   * Private-sale pricing: tiers use the official price; Grid Zero requires a
 *     manual owner price > 0 (no free grant).
 */
import type { ZoneKey } from "./mkt-format";

export type OwnerAction = "release" | "relock" | "private_sale";
export type ConfirmationTier = "normal" | "strong" | "maximum";

export const SLOT_STRONG_THRESHOLD = 1000;
export const GRID_ZERO_PHRASE = "GRID ZERO";

/** Confirmation law → the required confirmation tier for a target. */
export function confirmationTier(zone: ZoneKey | string, slots: number): ConfirmationTier {
  if (String(zone) === "grid_zero") return "maximum";
  if (Number(slots) > SLOT_STRONG_THRESHOLD) return "strong";
  return "normal";
}

export interface ConfirmationPlan {
  tier: ConfirmationTier;
  requireAck: boolean;
  requirePhrase: string | null;
  warning: string;
}

export function confirmationPlan(tier: ConfirmationTier, opts: { slots: number }): ConfirmationPlan {
  if (tier === "maximum") {
    return {
      tier,
      requireAck: true,
      requirePhrase: GRID_ZERO_PHRASE,
      warning:
        "GRID ZERO is the most sensitive inventory on THE GRID. Maximum confirmation is required regardless of quantity.",
    };
  }
  if (tier === "strong") {
    return {
      tier,
      requireAck: true,
      requirePhrase: String(Math.trunc(opts.slots)),
      warning: `This affects ${opts.slots} slots (over ${SLOT_STRONG_THRESHOLD}). Re-enter the exact slot count to proceed.`,
    };
  }
  return {
    tier,
    requireAck: true,
    requirePhrase: null,
    warning: "Please confirm this cell-authoritative owner action.",
  };
}

/** Whether the confirm control may be enabled given the current dialog inputs. */
export function canConfirm(plan: ConfirmationPlan, state: { acknowledged: boolean; typed: string }): boolean {
  if (plan.requireAck && !state.acknowledged) return false;
  if (plan.requirePhrase != null && state.typed.trim() !== plan.requirePhrase) return false;
  return true;
}

export interface ActionAvailability {
  available: boolean;
  reason: string | null;
}

/** Fail-closed capability read: missing / unknown / disabled all → unavailable. */
export function actionAvailability(
  capabilities: { actions?: Record<string, { available: boolean; reason?: string | null }> } | null | undefined,
  action: OwnerAction,
): ActionAvailability {
  const a = capabilities?.actions?.[action];
  if (!a) return { available: false, reason: "capability_unknown" };
  return { available: !!a.available, reason: a.available ? null : (a.reason ?? "unavailable") };
}

/** Frozen law: tier prices are official (auto); Grid Zero requires a manual price > 0. */
export function validatePrivateSalePrice(
  zone: ZoneKey | string,
  priceMicro: number | null,
): { ok: boolean; reason: string | null } {
  if (String(zone) === "grid_zero") {
    if (priceMicro == null || !(priceMicro > 0)) return { ok: false, reason: "grid_zero_price_required" };
    return { ok: true, reason: null };
  }
  return { ok: true, reason: null };
}

/**
 * Private-sale cancel eligibility — mirrors the backend law EXACTLY: a sale is
 * cancel-eligible ONLY while OPEN/ACTIVE. Once it is completed, expired or already
 * cancelled it can never be cancelled again (monotonic terminal states).
 */
export function canCancelPrivateSale(status: string): boolean {
  return status === "active";
}

/**
 * Owner-action parameter model (Macro #29 — Owner Console live wiring FIX).
 *
 * ROOT CAUSE of Claudio's "'reservation_id' / 'x' is required and must be an
 * integer" 422s: the console previously POSTed only {action, zone, slots} and
 * never carried the authoritative integer parameters the backend owner service
 * requires. These pure helpers are the SINGLE source of truth for building and
 * validating the exact body the backend ``/api/v1/owner/actions/*`` routes read
 * (release→reservation_id; relock→x,y,w,h; private_sale→sale_id,buyer,x,y,w,h,
 * unit_price_micro,expiry_real_ts).
 */
export interface OwnerActionParams {
  reservationId?: string; // release
  x?: string;
  y?: string;
  w?: string;
  h?: string; // relock + private_sale rectangle
  saleId?: string;
  buyer?: string;
  unitPriceMicro?: string;
  expiryRealTs?: string; // private_sale (epoch seconds, string)
  zone?: ZoneKey; // confirmation tier + Grid-Zero manual pricing law
}

export interface OwnerParamsValidation {
  ok: boolean;
  errors: Record<string, string>;
  slots: number;
  zone: ZoneKey;
}

const UINT_RE = /^\d+$/;
const ADDR_RE = /^0x[0-9a-fA-F]{40}$/;

function isUint(v: string | undefined): boolean {
  return typeof v === "string" && UINT_RE.test(v.trim());
}
function isPositive(v: string | undefined): boolean {
  return isUint(v) && Number(v) > 0;
}

/** Validate the owner-action inputs. Returns the derived slots + zone used by the
 *  confirmation law, plus a per-field error map (empty when ok). */
export function validateOwnerActionParams(action: OwnerAction, p: OwnerActionParams): OwnerParamsValidation {
  const errors: Record<string, string> = {};
  const zone: ZoneKey = (p.zone as ZoneKey) ?? "standard";

  if (action === "release") {
    if (!isUint(p.reservationId)) errors.reservationId = "reservation_id must be a non-negative integer";
    return { ok: Object.keys(errors).length === 0, errors, slots: 1, zone };
  }

  // relock + private_sale share the rectangle contract
  if (!isUint(p.x)) errors.x = "x must be a non-negative integer";
  if (!isUint(p.y)) errors.y = "y must be a non-negative integer";
  if (!isPositive(p.w)) errors.w = "w must be a positive integer";
  if (!isPositive(p.h)) errors.h = "h must be a positive integer";
  const slots = isPositive(p.w) && isPositive(p.h) ? Number(p.w) * Number(p.h) : 0;

  if (action === "private_sale") {
    if (!p.saleId || !p.saleId.trim()) errors.saleId = "sale_id is required";
    if (!p.buyer || !ADDR_RE.test(p.buyer.trim())) errors.buyer = "buyer must be a 0x wallet address";
    if (!isPositive(p.unitPriceMicro)) errors.unitPriceMicro = "unit price must be > 0 (no free grant)";
    if (!isUint(p.expiryRealTs)) errors.expiryRealTs = "expiry is required";
    else if (Number(p.expiryRealTs) <= Math.floor(Date.now() / 1000)) errors.expiryRealTs = "expiry must be in the future";
    const priceLaw = validatePrivateSalePrice(zone, isUint(p.unitPriceMicro) ? Number(p.unitPriceMicro) : null);
    if (!priceLaw.ok) errors.unitPriceMicro = priceLaw.reason ?? "invalid price";
  }

  return { ok: Object.keys(errors).length === 0, errors, slots, zone };
}

/** Build the EXACT integer-typed body the backend owner routes read. Only call
 *  with params that already passed {@link validateOwnerActionParams}. */
export function buildOwnerActionBody(action: OwnerAction, p: OwnerActionParams): Record<string, unknown> {
  if (action === "release") {
    return { reservation_id: Number(p.reservationId) };
  }
  const rect = { x: Number(p.x), y: Number(p.y), w: Number(p.w), h: Number(p.h) };
  if (action === "relock") return rect;
  return {
    sale_id: String(p.saleId).trim(),
    buyer: String(p.buyer).trim().toLowerCase(),
    ...rect,
    unit_price_micro: Number(p.unitPriceMicro),
    expiry_real_ts: Number(p.expiryRealTs),
  };
}
